import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import {
  MEETING_UNGROUPED_ID,
  applyMeetingMediaDrag,
  buildMeetingMediaBoard,
  canRemoveMeetingMediaGroup,
  mediaGroupsFromBoard,
  mediaKindFromMime,
  newMeetingMediaGroupId,
} from "../../utils/meetingRecorder";
import {
  meetingMediaGroupCardSx,
  meetingMediaGroupsFeedSx,
  meetingMediaThumbMediaSx,
  meetingEmptyGroupsDockSx,
} from "./meetingUiStyles";
import MeetingVideoThumb from "./MeetingVideoThumb";

const stopDragEvent = (e) => {
  e.stopPropagation();
};

function MediaTeamNoteEditor({ photo, onSaveTeamNote, t }) {
  const theme = useTheme();
  const [draft, setDraft] = useState(() => String(photo?.teamNote || ""));
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setDraft(String(photo?.teamNote || ""));
  }, [photo?.id, photo?.teamNote]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const dirty = draft !== String(photo?.teamNote || "");

  const save = async (value) => {
    if (typeof onSaveTeamNote !== "function") return;
    setSaving(true);
    try {
      await onSaveTeamNote(photo.id, value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{ mt: 1 }}
      onMouseDown={stopDragEvent}
      onTouchStart={stopDragEvent}
    >
      <TextField
        value={draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            if (next !== String(photo?.teamNote || "")) {
              save(next);
            }
          }, 700);
        }}
        onBlur={() => {
          if (timerRef.current) clearTimeout(timerRef.current);
          if (dirty) save(draft);
        }}
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        size="small"
        label={t("Meetings.teamNote")}
        placeholder={t("Meetings.teamNoteHint")}
        disabled={saving || typeof onSaveTeamNote !== "function"}
        sx={{
          "& .MuiOutlinedInput-root": {
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 2,
          },
        }}
      />
      {dirty || saving ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
          <Button
            size="small"
            startIcon={<SaveOutlinedIcon />}
            disabled={saving || !dirty}
            onClick={() => {
              if (timerRef.current) clearTimeout(timerRef.current);
              save(draft);
            }}
          >
            {saving ? t("Meetings.teamNoteSaving") : t("Meetings.teamNoteSave")}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}

function MediaCard({
  photo,
  provided,
  isDragging,
  notesById,
  audioByPhotoId,
  highlightPhotoId,
  expandedNoteIds,
  setExpandedNoteIds,
  openMedia,
  summary,
  onSaveTeamNote,
  onRequestDelete,
  selected,
  onToggleSelect,
  onOpenLinkedSample,
  t,
  theme,
}) {
  const isVideo =
    photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
  const highlighted = Number(highlightPhotoId) === Number(photo.id);
  const bucket = notesById.get(Number(photo.id));
  const audioLink = audioByPhotoId.get(Number(photo.id));
  const visual = isVideo ? bucket?.visual || [] : [];
  const spoken = [
    ...(bucket?.spoken || []),
    ...(!isVideo ? audioLink?.spoken || [] : []),
  ];
  const linked = bucket?.linked || [];
  const hasNotes = visual.length > 0 || spoken.length > 0 || linked.length > 0;
  const expanded = expandedNoteIds.has(Number(photo.id));
  const joinTexts = (arr) => arr.join("\n");
  const truncate = (text) => {
    if (expanded || text.length <= 140) return text;
    return `${text.slice(0, 140).trim()}…`;
  };
  const totalLen =
    joinTexts(visual).length +
    joinTexts(spoken).length +
    joinTexts(linked).length;
  const longNote = totalLen > 140;
  const audioSeq =
    audioLink?.chunk?.seq != null ? Number(audioLink.chunk.seq) + 1 : null;

  const noteSx = {
    whiteSpace: "pre-wrap",
    lineHeight: 1.45,
    overflowWrap: "anywhere",
    wordBreak: "normal",
    fontSize: "0.8125rem",
    color: "text.secondary",
  };

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      data-meeting-photo-id={photo.id}
      sx={{
        ...meetingMediaGroupCardSx(theme, highlighted || selected),
        opacity: isDragging ? 0.35 : 1,
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <Box sx={{ position: "relative", minWidth: 0, alignSelf: "start" }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggleSelect?.(photo.id)}
          onClick={stopDragEvent}
          onMouseDown={stopDragEvent}
          onTouchStart={stopDragEvent}
          inputProps={{
            "aria-label": t("Meetings.selectMediaItem"),
          }}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.35,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.common.white, 0.92),
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            "&:hover": {
              bgcolor: alpha(theme.palette.common.white, 1),
            },
          }}
        />
        <Box
          onClick={() => openMedia(photo.id)}
          sx={{
            position: "relative",
            cursor: "pointer",
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
            aspectRatio: "1 / 1",
          }}
        >
          {isVideo ? (
            <MeetingVideoThumb
              fileUrl={photo.fileUrl}
              sx={{ width: "100%", height: "100%", aspectRatio: "unset" }}
            />
          ) : (
            <Box
              component="img"
              src={meetingMediaUrl(photo.fileUrl)}
              alt={photo.caption || ""}
              loading="lazy"
              sx={meetingMediaThumbMediaSx}
            />
          )}
          <Chip
            size="small"
            label={photo.pendingUpload ? "…" : `#${photo.id}`}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              height: 22,
              fontWeight: 800,
              bgcolor: "rgba(0,0,0,0.72)",
              color: "#fff",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          {onRequestDelete ? (
            <IconButton
              size="small"
              aria-label={t("Meetings.deleteMedia")}
              onClick={(e) => {
                stopDragEvent(e);
                onRequestDelete(photo);
              }}
              onMouseDown={stopDragEvent}
              onTouchStart={stopDragEvent}
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                zIndex: 2,
                p: 0.4,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.error.main, 0.92),
                color: "common.white",
                boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                "&:hover": {
                  bgcolor: "error.dark",
                },
              }}
            >
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        {(() => {
          const links =
            Array.isArray(photo.linkedMuestras) && photo.linkedMuestras.length
              ? photo.linkedMuestras
              : photo.idMuestra && photo.muestraNombre
                ? [
                    {
                      idMuestra: photo.idMuestra,
                      muestraNombre: photo.muestraNombre,
                    },
                  ]
                : [];
          if (!links.length) return null;
          return (
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={0.5}
              sx={{ mb: 0.65 }}
            >
              {links.map((link) => (
                <Chip
                  key={link.idMuestra}
                  size="small"
                  color="primary"
                  label={link.muestraNombre || `#${link.idMuestra}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenLinkedSample?.(link.idMuestra);
                  }}
                  onMouseDown={stopDragEvent}
                  onTouchStart={stopDragEvent}
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      px: 0.75,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  title={t("Meetings.linkedSampleHint", {
                    name: link.muestraNombre || link.idMuestra,
                  })}
                />
              ))}
            </Stack>
          );
        })()}

        <Stack
          direction="row"
          alignItems="baseline"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 0.35 }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, lineHeight: 1.25 }}
          >
            {isVideo ? t("Meetings.video") : t("Meetings.photo")} #{photo.id}
          </Typography>
          {photo.offsetLabel ? (
            <Typography variant="caption" color="text.secondary">
              {photo.offsetLabel}
            </Typography>
          ) : null}
          {!isVideo && audioSeq != null ? (
            <Typography
              variant="caption"
              color="primary"
              sx={{ fontWeight: 700 }}
            >
              {t("Meetings.photoLinkedAudio", { n: audioSeq })}
            </Typography>
          ) : null}
        </Stack>

        {hasNotes ? (
          <Stack spacing={0.65} sx={{ mt: 0.35, flex: 1 }}>
            {spoken.length ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    letterSpacing: 0.2,
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                >
                  {t("Meetings.mediaNoteSpoken")}
                </Typography>
                <Typography variant="body2" sx={noteSx}>
                  {truncate(joinTexts(spoken))}
                </Typography>
              </Box>
            ) : null}
            {visual.length ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    letterSpacing: 0.2,
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                  }}
                >
                  {t("Meetings.mediaNoteVisual")}
                </Typography>
                <Typography variant="body2" sx={noteSx}>
                  {truncate(joinTexts(visual))}
                </Typography>
              </Box>
            ) : null}
            {linked.length ? (
              <Typography variant="body2" sx={noteSx}>
                {truncate(joinTexts(linked))}
              </Typography>
            ) : null}
            {longNote ? (
              <Button
                size="small"
                sx={{ alignSelf: "flex-start", px: 0, minWidth: 0, mt: -0.25 }}
                onMouseDown={stopDragEvent}
                onTouchStart={stopDragEvent}
                onClick={() => {
                  setExpandedNoteIds((prev) => {
                    const next = new Set(prev);
                    const idNum = Number(photo.id);
                    if (next.has(idNum)) next.delete(idNum);
                    else next.add(idNum);
                    return next;
                  });
                }}
              >
                {expanded
                  ? t("Meetings.mediaNoteLess")
                  : t("Meetings.mediaNoteMore")}
              </Button>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mt: 0.35, ...noteSx }}>
            {!isVideo && audioSeq != null
              ? t("Meetings.mediaNoteAudioPending")
              : summary && summary.status !== "error"
                ? t("Meetings.mediaNoteEmpty")
                : t("Meetings.mediaNotePending")}
          </Typography>
        )}

        <MediaTeamNoteEditor
          photo={photo}
          onSaveTeamNote={onSaveTeamNote}
          t={t}
        />
      </Box>
    </Box>
  );
}

export default forwardRef(function MeetingMediaGroupsFeed(
  {
    photos,
    mediaGroups,
    notesById,
    audioByPhotoId,
    highlightPhotoId,
    expandedNoteIds,
    setExpandedNoteIds,
    openMedia,
    summary,
    onSaveGroups,
    savingGroups = false,
    onSaveTeamNote,
    onDeleteMedia,
    onCreateSample,
    onLinkSample,
    onOpenLinkedSample,
    onSelectionChange,
    /** Host DOM (columna izquierda) para el dock de grupos abiertos. */
    floatingDockHost = null,
  },
  ref,
) {
  const { t } = useTranslation();
  const theme = useTheme();
  const saveTimerRef = useRef(null);
  const serverBoardKeyRef = useRef("");
  const [columns, setColumns] = useState(() =>
    buildMeetingMediaBoard(photos, mediaGroups),
  );
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  /** Grupos que siguen en la bandeja flotante hasta pulsar «Guardar». */
  const [floatingGroupIds, setFloatingGroupIds] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [deletingMedia, setDeletingMedia] = useState(false);

  useEffect(() => {
    const idsKey = (photos || []).map((p) => p.id).join(",");
    const linkKey = (photos || [])
      .map((p) => {
        const links =
          Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
            ? p.linkedMuestras
                .map((l) => `${l.idMuestra}:${l.muestraNombre || ""}`)
                .join(",")
            : `${p.idMuestra || ""}:${p.muestraNombre || ""}`;
        return `${p.id}:${links}:${String(p.teamNote || "").length}`;
      })
      .join(",");
    const key = `${idsKey}|${linkKey}|${JSON.stringify(mediaGroups || [])}`;
    if (key === serverBoardKeyRef.current) return;
    serverBoardKeyRef.current = key;
    setColumns(buildMeetingMediaBoard(photos, mediaGroups));
  }, [photos, mediaGroups]);

  useEffect(() => {
    const alive = new Set((photos || []).map((p) => Number(p.id)));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (alive.has(Number(id))) next.add(Number(id));
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [photos]);

  const persist = (nextColumns) => {
    if (typeof onSaveGroups !== "function") return;
    const payload = mediaGroupsFromBoard(nextColumns);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      onSaveGroups(payload);
    }, 350);
  };

  const flushSaveGroups = useCallback(() => {
    if (typeof onSaveGroups !== "function") {
      return Promise.resolve(mediaGroupsFromBoard(columns));
    }
    const payload = mediaGroupsFromBoard(columns);
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    return Promise.resolve(onSaveGroups(payload)).then(() => payload);
  }, [columns, onSaveGroups]);

  const onDragEnd = (result) => {
    const next = applyMeetingMediaDrag(columns, result);
    if (!next) return;
    setColumns(next);
    persist(next);
  };

  const removeGroup = (groupId) => {
    const idx = columns.findIndex((c) => c.id === groupId);
    if (idx < 0) return;
    const removed = columns[idx];
    if (!canRemoveMeetingMediaGroup(removed)) return;
    const next = columns.filter((c) => c.id !== groupId);
    setColumns(next);
    setFloatingGroupIds((prev) => {
      if (!prev.has(groupId)) return prev;
      const nextSet = new Set(prev);
      nextSet.delete(groupId);
      return nextSet;
    });
    persist(next);
  };

  const requestRemoveGroup = (col) => {
    if (!canRemoveMeetingMediaGroup(col)) return;
    setDeleteGroupTarget(col);
  };

  const confirmRemoveGroup = () => {
    if (!deleteGroupTarget?.id) return;
    removeGroup(deleteGroupTarget.id);
    setDeleteGroupTarget(null);
  };

  const pinFloatingGroup = (groupId) => {
    setFloatingGroupIds((prev) => {
      if (!prev.has(groupId)) return prev;
      const nextSet = new Set(prev);
      nextSet.delete(groupId);
      return nextSet;
    });
  };

  const openFloatingGroup = (groupId) => {
    setFloatingGroupIds((prev) => {
      if (prev.has(groupId)) return prev;
      const nextSet = new Set(prev);
      nextSet.add(groupId);
      return nextSet;
    });
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.scrollBy({ top: -40, behavior: "smooth" });
      });
    }
  };

  const setGroupLabel = (groupId, label) => {
    const next = columns.map((c) => (c.id === groupId ? { ...c, label } : c));
    setColumns(next);
    persist(next);
  };

  const toggleSelect = (photoId) => {
    const id = Number(photoId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectGroupPhotos = (groupPhotos, selectAll) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of groupPhotos || []) {
        const id = Number(p.id);
        if (selectAll) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const selectedOrderedPhotos = useMemo(() => {
    const out = [];
    for (const col of columns) {
      for (const p of col.photos || []) {
        if (selectedIds.has(Number(p.id))) out.push(p);
      }
    }
    return out;
  }, [columns, selectedIds]);

  const total = useMemo(
    () => columns.reduce((n, c) => n + (c.photos?.length || 0), 0),
    [columns],
  );

  const floatingGroups = useMemo(
    () => columns.filter((c) => !c.ungrouped && floatingGroupIds.has(c.id)),
    [columns, floatingGroupIds],
  );
  const restColumns = useMemo(
    () => columns.filter((c) => c.ungrouped || !floatingGroupIds.has(c.id)),
    [columns, floatingGroupIds],
  );

  const addGroup = useCallback(() => {
    const toMove = selectedOrderedPhotos;
    const moveIds = new Set(toMove.map((p) => Number(p.id)));
    const newId = newMeetingMediaGroupId();
    setColumns((prev) => {
      const namedCount = prev.filter((c) => !c.ungrouped).length;
      const stripped = prev.map((c) => ({
        ...c,
        photos: moveIds.size
          ? (c.photos || []).filter((p) => !moveIds.has(Number(p.id)))
          : c.photos || [],
      }));
      const next = [
        {
          id: newId,
          label: t("Meetings.mediaGroupDefault", { n: namedCount + 1 }),
          photos: toMove.length ? [...toMove] : [],
        },
        ...stripped,
      ];
      if (typeof onSaveGroups === "function") {
        const payload = mediaGroupsFromBoard(next);
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
          onSaveGroups(payload);
        }, 350);
      }
      return next;
    });
    setFloatingGroupIds((prev) => {
      const nextSet = new Set(prev);
      nextSet.add(newId);
      return nextSet;
    });
    if (moveIds.size) setSelectedIds(new Set());
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`meeting-media-group-${newId}`);
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    }
  }, [t, onSaveGroups, selectedOrderedPhotos]);

  // Si un grupo flotante desaparece del board, limpiar el set
  useEffect(() => {
    const alive = new Set(columns.map((c) => c.id));
    setFloatingGroupIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (alive.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [columns]);

  const createSampleFromSelection = useCallback(() => {
    if (!selectedOrderedPhotos.length || !onCreateSample) return;
    onCreateSample(selectedOrderedPhotos);
  }, [onCreateSample, selectedOrderedPhotos]);

  const linkSampleFromSelection = useCallback(() => {
    if (!selectedOrderedPhotos.length || !onLinkSample) return;
    onLinkSample(selectedOrderedPhotos);
  }, [onLinkSample, selectedOrderedPhotos]);

  useImperativeHandle(
    ref,
    () => ({
      addGroup,
      createSample: createSampleFromSelection,
      linkSample: linkSampleFromSelection,
      getSelectedCount: () => selectedOrderedPhotos.length,
      getMediaGroups: () => mediaGroupsFromBoard(columns),
      flushSaveGroups,
    }),
    [
      addGroup,
      createSampleFromSelection,
      linkSampleFromSelection,
      selectedOrderedPhotos.length,
      columns,
      flushSaveGroups,
    ],
  );

  useEffect(() => {
    onSelectionChange?.(selectedOrderedPhotos.length);
  }, [selectedOrderedPhotos.length, onSelectionChange]);

  const confirmDeleteMedia = async () => {
    if (!deleteTarget?.id || !onDeleteMedia || deletingMedia) return;
    setDeletingMedia(true);
    try {
      await onDeleteMedia(deleteTarget.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(Number(deleteTarget.id));
        return next;
      });
      setDeleteTarget(null);
    } catch (_) {
      // error ya mostrado en la página
    } finally {
      setDeletingMedia(false);
    }
  };

  const deleteTargetIsVideo =
    deleteTarget &&
    (deleteTarget.kind === "video" ||
      mediaKindFromMime(deleteTarget.mimeType) === "video");

  const renderGroupColumn = (col, { floating = false, aside = false } = {}) => {
    // Bloqueado = no se puede sacar/reordenar piezas del grupo hasta Abrir.
    // Siempre se puede soltar media nueva (si no, tras Guardar parece que el grupo no funciona).
    const locked = !col.ungrouped && !floating;
    const groupIds = (col.photos || []).map((p) => Number(p.id));
    const allSelected =
      groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));
    const someSelected =
      !allSelected && groupIds.some((id) => selectedIds.has(id));

    return (
      <Box
        key={col.id}
        id={`meeting-media-group-${col.id}`}
        sx={{
          border: "1px solid",
          borderColor: locked
            ? "divider"
            : floating
              ? "primary.light"
              : "divider",
          borderRadius: 2,
          p: 1,
          bgcolor: col.ungrouped
            ? "transparent"
            : locked
              ? "action.disabledBackground"
              : "action.hover",
          opacity: locked ? 0.92 : 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {col.photos.length ? (
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => selectGroupPhotos(col.photos, !allSelected)}
              inputProps={{
                "aria-label": t("Meetings.selectGroup"),
              }}
              sx={{ p: 0.5 }}
            />
          ) : null}
          {col.ungrouped ? (
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
              {t("Meetings.mediaUngrouped")}
              {` · ${col.photos.length}`}
            </Typography>
          ) : (
            <>
              {locked ? (
                <LockOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", flexShrink: 0 }}
                />
              ) : null}
              <TextField
                size="small"
                value={col.label}
                onChange={(e) => setGroupLabel(col.id, e.target.value)}
                placeholder={t("Meetings.mediaGroupLabelPlaceholder")}
                sx={{ flex: 1 }}
              />
              {floating ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveOutlinedIcon />}
                  onClick={() => pinFloatingGroup(col.id)}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Meetings.mediaGroupSave")}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LockOpenOutlinedIcon />}
                  onClick={() => openFloatingGroup(col.id)}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Meetings.mediaGroupOpen")}
                </Button>
              )}
              <Tooltip
                title={
                  canRemoveMeetingMediaGroup(col)
                    ? t("Meetings.mediaGroupRemove")
                    : t("Meetings.mediaGroupRemoveBlocked")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => requestRemoveGroup(col)}
                    disabled={!canRemoveMeetingMediaGroup(col)}
                    aria-label={t("Meetings.mediaGroupRemove")}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
        <Droppable
          droppableId={col.id}
          direction="vertical"
          isDropDisabled={false}
          renderClone={(provided, snapshot, rubric) => {
            const photo = (col.photos || [])[rubric.source.index];
            if (!photo) return <div ref={provided.innerRef} />;
            return (
              <MediaCard
                photo={photo}
                provided={provided}
                isDragging={snapshot.isDragging}
                notesById={notesById}
                audioByPhotoId={audioByPhotoId}
                highlightPhotoId={highlightPhotoId}
                expandedNoteIds={expandedNoteIds}
                setExpandedNoteIds={setExpandedNoteIds}
                openMedia={openMedia}
                summary={summary}
                onSaveTeamNote={onSaveTeamNote}
                onRequestDelete={undefined}
                selected={selectedIds.has(Number(photo.id))}
                onToggleSelect={undefined}
                onOpenLinkedSample={onOpenLinkedSample}
                t={t}
                theme={theme}
              />
            );
          }}
        >
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                ...meetingMediaGroupsFeedSx,
                ...(aside
                  ? {
                      gridTemplateColumns: "1fr",
                      gap: 1,
                    }
                  : null),
                minHeight: col.photos.length ? 48 : aside ? 120 : 88,
                p: col.photos.length ? 0 : 1,
                bgcolor: snapshot.isDraggingOver
                  ? "action.selected"
                  : col.ungrouped
                    ? "transparent"
                    : "background.paper",
                outline: snapshot.isDraggingOver
                  ? `2px dashed ${theme.palette.primary.main}`
                  : locked
                    ? `1px solid ${theme.palette.divider}`
                    : col.photos.length
                      ? "none"
                      : `1px dashed ${theme.palette.divider}`,
                outlineOffset: 2,
                borderRadius: 1,
              }}
            >
              {col.photos.map((photo, index) => (
                <Draggable
                  key={String(photo.id)}
                  draggableId={`media-${photo.id}`}
                  index={index}
                  isDragDisabled={locked}
                >
                  {(dragProvided, dragSnapshot) => (
                    <MediaCard
                      photo={photo}
                      provided={dragProvided}
                      isDragging={dragSnapshot.isDragging}
                      notesById={notesById}
                      audioByPhotoId={audioByPhotoId}
                      highlightPhotoId={highlightPhotoId}
                      expandedNoteIds={expandedNoteIds}
                      setExpandedNoteIds={setExpandedNoteIds}
                      openMedia={openMedia}
                      summary={summary}
                      onSaveTeamNote={onSaveTeamNote}
                      onRequestDelete={
                        onDeleteMedia ? (p) => setDeleteTarget(p) : undefined
                      }
                      selected={selectedIds.has(Number(photo.id))}
                      onToggleSelect={toggleSelect}
                      onOpenLinkedSample={onOpenLinkedSample}
                      t={t}
                      theme={theme}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {!col.photos.length ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ p: 1 }}
                >
                  {locked
                    ? t("Meetings.mediaGroupLockedEmpty")
                    : t("Meetings.mediaGroupEmpty")}
                </Typography>
              ) : locked ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ gridColumn: "1 / -1", px: 0.5, pt: 0.5 }}
                >
                  {t("Meetings.mediaGroupLockedHint")}
                </Typography>
              ) : null}
            </Box>
          )}
        </Droppable>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
        {t("Meetings.mediaGallery")}
        {` · ${total}`}
        {savingGroups ? ` · ${t("Meetings.mediaGroupsSaving")}` : ""}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {t("Meetings.mediaGroupsHint")}
      </Typography>
      {selectedOrderedPhotos.length ? (
        <Typography
          variant="caption"
          color="primary"
          display="block"
          sx={{ mb: 1, fontWeight: 700 }}
        >
          {t("Meetings.selectedMediaCount", {
            count: selectedOrderedPhotos.length,
          })}
        </Typography>
      ) : null}

      <DragDropContext onDragEnd={onDragEnd}>
        {(() => {
          if (!floatingGroups.length) return null;
          const useAside = Boolean(floatingDockHost);
          const dock = (
            <Box
              sx={
                useAside
                  ? meetingEmptyGroupsDockSx(theme, { sticky: false })
                  : meetingEmptyGroupsDockSx(theme, { sticky: true })
              }
            >
              <Typography
                variant="caption"
                color="primary"
                sx={{ fontWeight: 800, display: "block", mb: 1 }}
              >
                {useAside
                  ? t("Meetings.mediaGroupAsideHint")
                  : t("Meetings.mediaGroupStickyHint")}
              </Typography>
              <Stack spacing={1.5}>
                {floatingGroups.map((col) =>
                  renderGroupColumn(col, {
                    floating: true,
                    aside: useAside,
                  }),
                )}
              </Stack>
            </Box>
          );
          if (useAside) {
            return createPortal(dock, floatingDockHost);
          }
          return dock;
        })()}
        <Stack spacing={1.5}>
          {restColumns.map((col) => renderGroupColumn(col))}
        </Stack>
      </DragDropContext>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deletingMedia && setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>
          {deleteTargetIsVideo
            ? t("Meetings.deleteMediaVideoTitle")
            : t("Meetings.deleteMediaPhotoTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.deleteMediaBody", {
              id: deleteTarget?.id,
              kind: deleteTargetIsVideo
                ? t("Meetings.video")
                : t("Meetings.photo"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deletingMedia}
          >
            {t("Meetings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletingMedia}
            onClick={confirmDeleteMedia}
            startIcon={
              deletingMedia ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineOutlinedIcon />
              )
            }
          >
            {t("Meetings.deleteMediaConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteGroupTarget)}
        onClose={() => setDeleteGroupTarget(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.mediaGroupRemoveConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.mediaGroupRemoveConfirmBody", {
              name:
                String(deleteGroupTarget?.label || "").trim() ||
                deleteGroupTarget?.id ||
                "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteGroupTarget(null)}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmRemoveGroup}
            startIcon={<DeleteOutlineOutlinedIcon />}
          >
            {t("Meetings.mediaGroupRemoveConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
